import { DEVICE_TYPE } from './Device';

function generateDropdownOptionValues({
  $t, list, i18n, eventsWithIcon, supportDeviceMap = {}
}) {
  return list.reduce((init, event) => {
    const text = i18n[event]($t);
    const supportDevice = supportDeviceMap[event] || [];

    init.push({
      text,
      value: event,
      displayIcon: eventsWithIcon.has(event),
      supportDevice
    });
    return init;
  }, []);
}

const EVENT_LABELS = Object.freeze({
  DEVICE_EVENTS: (t) => t('Device events'),
  VCA: (t) => t('Video Content Analysis events'),
  GENERAL_EVENTS: (t) => t('General events'),
  ONVIF_EVENTS: (t) => t('Generic ONVIF events'),
  SYSTEM_EVENTS: (t) => t('System events'),
  GENERAL_SYSTEM_EVENTS: (t) => t('General system events'),
  CAMERA_SYSTEM_EVENTS: (t) => t('Camera system events'),
  ACCESS_EVENTS: (t) => t('Access events'),
  SENSOR_EVENTS: (t) => t('Sensor events'),
  LICENSE_PLATE_EVENTS: (t) => t('License Plate events'),
  FACE_RECOGNITION_EVENTS: (t) => t('Facial events'),
  NVR_VSS_SYSTEM_EVENTS: (t, allowVssPrompt) => (allowVssPrompt ? t('NVR/VSS system events') : t('NVR system events')),
});

const EVENT_TIME_RANGE = Object.freeze({
  KEEP_DURATION: 7 * 24 * 60 * 60 * 1000,
  PLAY_DURATION: 13 * 1000,
  PLAY_BACKWARD_TIME: 4 * 1000,
  PLAY_FORWARD_TIME: 8 * 1000,
});

const GENERAL_SYSTEM_EVENT_TYPE = Object.freeze({
  CONNECT: 'Connect',
  DISCONNECT: 'Disconnect',
  FIRMWARE_UPGRADE_FAILED: 'FirmwareUpgradeFail',
});

const SYSTEM_EVENT_TYPES = Object.freeze({
  RECORDING_ERROR: 'RecordingError',
  SD_CARD_ERROR: 'SDCardError',
  SD_CARD_LIFE_WARNING: 'SDCardLifeExpectancy',
});

const VSS_VCA_EVENT_TYPE = Object.freeze({
  FACE_DETECTION: 'Face',
  CROWD_DETECTION: 'Crowd',
  RUNNING_DETECTION: 'Running',
  RESTRICTED_ZONE: 'RestrictedZone',
  PARKING_VIOLATION: 'ParkingViolation',
  SMART_TRACKING_OBJECT: 'SmartTracking',
  MOTION_DETECTION: 'SmartMotion',
});

const VSS_GENERAL_EVENT_TYPES = Object.freeze({
  CAMERA_DIGITAL_OUTPUT: 'DigitalOutput',
  PIR: 'PIR',
});

const VSS_DEVICE_EVENT = Object.freeze({
  I18N: {
    [VSS_VCA_EVENT_TYPE.FACE_DETECTION]: (t) => `${t('Face detection')}`,
    [VSS_VCA_EVENT_TYPE.CROWD_DETECTION]: (t) => `${t('Crowd detection')}`,
    [VSS_VCA_EVENT_TYPE.RUNNING_DETECTION]: (t) => `${t('Running detection')}`,
    [VSS_VCA_EVENT_TYPE.RESTRICTED_ZONE]: (t) => `${t('Restricted zone')}`,
    [VSS_VCA_EVENT_TYPE.PARKING_VIOLATION]: (t) => `${t('Parking violation')}`,
    [VSS_VCA_EVENT_TYPE.SMART_TRACKING_OBJECT]: (t) => `${t('Smart tracking an object')}`,
    [VSS_VCA_EVENT_TYPE.MOTION_DETECTION]: (t) => `${t('Motion detection')}`,
    [VSS_GENERAL_EVENT_TYPES.CAMERA_DIGITAL_OUTPUT]: (t) => `${t('Camera DO')}`,
    [VSS_GENERAL_EVENT_TYPES.PIR]: (t) => `${t('PIR')}`,
  },
  ICONS: {
    [VSS_VCA_EVENT_TYPE.FACE_DETECTION]: 'img_app_7.png',
    [VSS_VCA_EVENT_TYPE.CROWD_DETECTION]: 'img_app_5.png',
    [VSS_VCA_EVENT_TYPE.RUNNING_DETECTION]: 'img_app_6.png',
    [VSS_VCA_EVENT_TYPE.RESTRICTED_ZONE]: 'img_app_15.png',
    [VSS_VCA_EVENT_TYPE.PARKING_VIOLATION]: 'img_app_17.png',
    [VSS_VCA_EVENT_TYPE.SMART_TRACKING_OBJECT]: 'img_app_16.png',
    [VSS_VCA_EVENT_TYPE.MOTION_DETECTION]: 'img_app_1.png',
    [VSS_GENERAL_EVENT_TYPES.CAMERA_DIGITAL_OUTPUT]: 'img_app_13.png',
    [VSS_GENERAL_EVENT_TYPES.PIR]: 'img_app_11.png',
  },
  VCA_EVENTS: Object.values(VSS_VCA_EVENT_TYPE),
  GENERAL_EVENTS: Object.values(VSS_GENERAL_EVENT_TYPES),
});

const VSS_SYSTEM_EVENT_TYPES = Object.freeze({
  CHANNEL_ONLINE: 'ChannelOnline',
  CHANNEL_OFFLINE: 'ChannelOffline',
  DISK_FAILURE: 'DiskFailure',
  DISK_FULL: 'DiskFull',
});

const VSS_SYSTEM_EVENT = Object.freeze({
  I18N: {
    [VSS_SYSTEM_EVENT_TYPES.CHANNEL_ONLINE]: (t) => `${t('Channel online')}`,
    [VSS_SYSTEM_EVENT_TYPES.CHANNEL_OFFLINE]: (t) => `${t('Channel offline')}`,
    [VSS_SYSTEM_EVENT_TYPES.DISK_FAILURE]: (t) => `${t('Disk failure')}`,
    [VSS_SYSTEM_EVENT_TYPES.DISK_FULL]: (t) => `${t('Disk full')}`,
  },
  ICONS: {
    [VSS_SYSTEM_EVENT_TYPES.CHANNEL_ONLINE]: 'status_line_online',
    [VSS_SYSTEM_EVENT_TYPES.CHANNEL_OFFLINE]: 'status_line_offline',
    [VSS_SYSTEM_EVENT_TYPES.DISK_FAILURE]: 'status_line_storage_alert',
    [VSS_SYSTEM_EVENT_TYPES.DISK_FULL]: 'status_line_storage_alert',
  },
  TYPES: VSS_SYSTEM_EVENT_TYPES,
  VSS_SYSTEM_EVENTS: Object.values(VSS_SYSTEM_EVENT_TYPES),
});

const NVR_VCA_EVENT_TYPES = Object.freeze({
  FACE_DETECTION: 'Face',
  CROWD_DETECTION: 'Crowd',
  RUNNING_DETECTION: 'Running',
  RESTRICTED_ZONE: 'RestrictedZone',
  PARKING_VIOLATION: 'ParkingViolation',
  SMART_TRACKING_OBJECT: 'SmartTracking',
  MOTION_DETECTION: 'SmartMotion',
});

const NVR_GENERAL_EVENT_TYPES = Object.freeze({
  CAMERA_DIGITAL_OUTPUT: 'DigitalOutput',
  PIR: 'PIR',
});

const NVR_ONVIF_EVENT_TYPES = Object.freeze({ GENERIC_ONVIF: 'GenericOnvif' });

const NVR_DEVICE_EVENT = Object.freeze({
  I18N: {
    [NVR_VCA_EVENT_TYPES.FACE_DETECTION]: (t) => `${t('Face detection')}`,
    [NVR_VCA_EVENT_TYPES.CROWD_DETECTION]: (t) => `${t('Crowd detection')}`,
    [NVR_VCA_EVENT_TYPES.RUNNING_DETECTION]: (t) => `${t('Running detection')}`,
    [NVR_VCA_EVENT_TYPES.RESTRICTED_ZONE]: (t) => `${t('Restricted zone')}`,
    [NVR_VCA_EVENT_TYPES.PARKING_VIOLATION]: (t) => `${t('Parking violation')}`,
    [NVR_VCA_EVENT_TYPES.SMART_TRACKING_OBJECT]: (t) => `${t('Smart tracking an object')}`,
    [NVR_VCA_EVENT_TYPES.MOTION_DETECTION]: (t) => `${t('Motion detection')}`,
    [NVR_GENERAL_EVENT_TYPES.CAMERA_DIGITAL_OUTPUT]: (t) => `${t('Camera DO')}`,
    [NVR_GENERAL_EVENT_TYPES.PIR]: (t) => `${t('PIR')}`,
    [NVR_ONVIF_EVENT_TYPES.GENERIC_ONVIF]: (t) => `${t('All generic ONVIF events')}`,
  },
  ICONS: {
    [NVR_VCA_EVENT_TYPES.FACE_DETECTION]: 'img_app_7.png',
    [NVR_VCA_EVENT_TYPES.CROWD_DETECTION]: 'img_app_5.png',
    [NVR_VCA_EVENT_TYPES.RUNNING_DETECTION]: 'img_app_6.png',
    [NVR_VCA_EVENT_TYPES.RESTRICTED_ZONE]: 'img_app_15.png',
    [NVR_VCA_EVENT_TYPES.PARKING_VIOLATION]: 'img_app_17.png',
    [NVR_VCA_EVENT_TYPES.SMART_TRACKING_OBJECT]: 'img_app_16.png',
    [NVR_VCA_EVENT_TYPES.MOTION_DETECTION]: 'img_app_1.png',
    [NVR_GENERAL_EVENT_TYPES.CAMERA_DIGITAL_OUTPUT]: 'img_app_13.png',
    [NVR_GENERAL_EVENT_TYPES.PIR]: 'img_app_11.png',
    [NVR_ONVIF_EVENT_TYPES.GENERIC_ONVIF]: 'img_app_onvif.svg',
  },
  NVR_VCA_EVENTS: Object.values(NVR_VCA_EVENT_TYPES),
  NVR_GENERAL_EVENTS: Object.values(NVR_GENERAL_EVENT_TYPES),
  NVR_ONVIF_EVENTS: Object.values(NVR_ONVIF_EVENT_TYPES),
});

const NVR_SYSTEM_EVENT_TYPES = Object.freeze({
  CHANNEL_ONLINE: 'ChannelOnline',
  CHANNEL_OFFLINE: 'ChannelOffline',
  DISK_FAILURE: 'DiskFailure',
  DISK_FULL: 'DiskFull',
  RECORDING_INTERRUPTED: 'RecordingInterrupted',
  SYSTEM_DI: 'SystemDigitalInput',
  SYSTEM_DO: 'SystemDigitalOutput',
  POE_ERROR: 'PoEError',
});

const NVR_SYSTEM_EVENT = Object.freeze({
  I18N: {
    [NVR_SYSTEM_EVENT_TYPES.CHANNEL_ONLINE]: (t) => `${t('channel online')}`,
    [NVR_SYSTEM_EVENT_TYPES.CHANNEL_OFFLINE]: (t) => `${t('channel offline')}`,
    [NVR_SYSTEM_EVENT_TYPES.SYSTEM_DI]: (t) => `${t('System DI')}`,
    [NVR_SYSTEM_EVENT_TYPES.SYSTEM_DO]: (t) => `${t('System DO')}`,
    [NVR_SYSTEM_EVENT_TYPES.DISK_FAILURE]: (t) => `${t('Disk failure')}`,
    [NVR_SYSTEM_EVENT_TYPES.DISK_FULL]: (t) => `${t('Disk full')}`,
    [NVR_SYSTEM_EVENT_TYPES.POE_ERROR]: (t) => `${t('PoE error')}`,
    [NVR_SYSTEM_EVENT_TYPES.RECORDING_INTERRUPTED]: (t) => `${t('Recording error')}`,
  },
  ICONS: {
    [NVR_SYSTEM_EVENT_TYPES.CHANNEL_ONLINE]: 'status_line_online',
    [NVR_SYSTEM_EVENT_TYPES.CHANNEL_OFFLINE]: 'status_line_offline',
    [NVR_SYSTEM_EVENT_TYPES.SYSTEM_DI]: 'status_line_device_DI_normal',
    [NVR_SYSTEM_EVENT_TYPES.SYSTEM_DO]: 'status_line_device_DO_normal',
    [NVR_SYSTEM_EVENT_TYPES.DISK_FAILURE]: 'status_line_storage_alert',
    [NVR_SYSTEM_EVENT_TYPES.DISK_FULL]: 'status_line_storage_alert',
    [NVR_SYSTEM_EVENT_TYPES.POE_ERROR]: 'status_line_PoE_alert',
    [NVR_SYSTEM_EVENT_TYPES.RECORDING_INTERRUPTED]: 'status_recording_alert_line',
  },
  TYPES: NVR_SYSTEM_EVENT_TYPES,
  NVR_SYSTEM_EVENTS: Object.values(NVR_SYSTEM_EVENT_TYPES),
});

const CAMERA_SYSTEM_EVENT = Object.freeze({
  I18N: {
    [GENERAL_SYSTEM_EVENT_TYPE.CONNECT]: (t) => `${t('Device online')}`,
    [GENERAL_SYSTEM_EVENT_TYPE.DISCONNECT]: (t) => `${t('Device offline')}`,
    [GENERAL_SYSTEM_EVENT_TYPE.FIRMWARE_UPGRADE_FAILED]: (t) => `${t('Firmware update failed')}`,
    [SYSTEM_EVENT_TYPES.SD_CARD_ERROR]: (t) => `${t('SD card error')}`,
    [SYSTEM_EVENT_TYPES.SD_CARD_LIFE_WARNING]: (t) => `${t('SD card life expectancy')}`,
    [SYSTEM_EVENT_TYPES.RECORDING_ERROR]: (t) => `${t('Recording error')}`,
  },
  ICONS: {
    [GENERAL_SYSTEM_EVENT_TYPE.CONNECT]: 'status_line_online',
    [GENERAL_SYSTEM_EVENT_TYPE.DISCONNECT]: 'status_line_offline',
    [GENERAL_SYSTEM_EVENT_TYPE.FIRMWARE_UPGRADE_FAILED]: 'status_firmware_alert_line',
    [SYSTEM_EVENT_TYPES.SD_CARD_ERROR]: 'status_sdcard_alert_line',
    [SYSTEM_EVENT_TYPES.SD_CARD_LIFE_WARNING]: 'status_sdcard_alert_line',
    [SYSTEM_EVENT_TYPES.RECORDING_ERROR]: 'status_recording_alert_line',
  },
  TYPES: {
    ...GENERAL_SYSTEM_EVENT_TYPE,
    ...SYSTEM_EVENT_TYPES
  },
  CAMERA_SYSTEM_EVENTS: Object.values(SYSTEM_EVENT_TYPES),
  GENERAL_EVENTS: Object.values(GENERAL_SYSTEM_EVENT_TYPE),
});

const VCA_EVENT_TYPE = Object.freeze({
  INTRUSION: 'Intrusion',
  LOITERING: 'Loitering',
  LINE_CROSSING: 'LineCrossing',
  MISSING_OBJECT: 'MissingObject',
  UNATTENDED_OBJECT: 'UnattendedObject',
  LICENSE_PLATE_RECOGNITION: 'LicensePlateRecognition',
  FACE_RECOGNITION: 'FaceRecognition',
  SPOT_OCCUPANCY: 'SpotOccupancy',
  PPE_DETECTION: 'PPEDetection',
  FALL_DETECTION: 'FallDetection',
  THINK_ALERT: 'ThinkAlert',
});

const GENERAL_DEVICE_EVENT_TYPE = Object.freeze({
  AUDIO: 'Audio',
  CAMERA_DIGITAL_INPUT: 'DigitalInput',
  TAMPERING: 'Tampering',
});

const CAMERA_DEVICE_EVENT = Object.freeze({
  VCA_EVENT_TYPE,
  GENERAL_DEVICE_EVENT_TYPE,
  I18N: {
    [GENERAL_DEVICE_EVENT_TYPE.AUDIO]: (t) => `${t('Audio detection')}`,
    [GENERAL_DEVICE_EVENT_TYPE.CAMERA_DIGITAL_INPUT]: (t) => `${t('Camera DI')}`,
    [GENERAL_DEVICE_EVENT_TYPE.TAMPERING]: (t) => `${t('Tampering detection')}`,
    [VCA_EVENT_TYPE.INTRUSION]: (t) => `${t('Intrusion detection')}`,
    [VCA_EVENT_TYPE.LINE_CROSSING]: (t) => `${t('Line crossing detection')}`,
    [VCA_EVENT_TYPE.LOITERING]: (t) => `${t('Loitering detection')}`,
    [VCA_EVENT_TYPE.MISSING_OBJECT]: (t) => `${t('Missing object detection')}`,
    [VCA_EVENT_TYPE.UNATTENDED_OBJECT]: (t) => `${t('Unattended object detection')}`,
    [VCA_EVENT_TYPE.LICENSE_PLATE_RECOGNITION]: (t) => `${t('License plate recognition')}`,
    [VCA_EVENT_TYPE.FACE_RECOGNITION]: (t) => `${t('Facial recognition')}`,
    [VCA_EVENT_TYPE.SPOT_OCCUPANCY]: (t) => `${t('Parking space detection')}`,
    [VCA_EVENT_TYPE.PPE_DETECTION]: (t) => `${t('PPE detection')}`,
    [VCA_EVENT_TYPE.THINK_ALERT]: (t) => `${t('Think alert')}`,
    [VCA_EVENT_TYPE.FALL_DETECTION]: (t) => `${t('Fall detection')}`,
  },
  ICONS: {
    [GENERAL_DEVICE_EVENT_TYPE.AUDIO]: 'img_app_10.png',
    [GENERAL_DEVICE_EVENT_TYPE.CAMERA_DIGITAL_INPUT]: 'img_app_13.png',
    [GENERAL_DEVICE_EVENT_TYPE.TAMPERING]: 'img_app_12.png',
    [VCA_EVENT_TYPE.INTRUSION]: 'img_app_2.png',
    [VCA_EVENT_TYPE.LOITERING]: 'img_app_4.png',
    [VCA_EVENT_TYPE.LINE_CROSSING]: 'img_app_3.png',
    [VCA_EVENT_TYPE.MISSING_OBJECT]: 'img_app_8.png',
    [VCA_EVENT_TYPE.UNATTENDED_OBJECT]: 'img_app_9.png',
    [VCA_EVENT_TYPE.LICENSE_PLATE_RECOGNITION]: 'img_app_19.png',
    [VCA_EVENT_TYPE.FACE_RECOGNITION]: 'img_app_18.png',
    [VCA_EVENT_TYPE.SPOT_OCCUPANCY]: 'img_app_20.png',
    [VCA_EVENT_TYPE.PPE_DETECTION]: 'img_app_21.png',
    [VCA_EVENT_TYPE.THINK_ALERT]: 'img_app_23.svg',
    [VCA_EVENT_TYPE.FALL_DETECTION]: 'img_app_22.svg',
  },
  CAMERA_VCA_EVENTS: Object.values(VCA_EVENT_TYPE),
  CAMERA_GENERAL_EVENTS: Object.values(GENERAL_DEVICE_EVENT_TYPE),
});

const DEVICE_EVENT = Object.freeze({
  ...CAMERA_DEVICE_EVENT,
  ...NVR_DEVICE_EVENT,
  ...VSS_DEVICE_EVENT,
  I18N: {
    ...CAMERA_DEVICE_EVENT.I18N,
    ...NVR_DEVICE_EVENT.I18N,
    ...VSS_DEVICE_EVENT.I18N,
  },
  ICONS: {
    ...CAMERA_DEVICE_EVENT.ICONS,
    ...NVR_DEVICE_EVENT.ICONS,
    ...VSS_DEVICE_EVENT.ICONS,
  },
  VCA_EVENTS: [...new Set([...CAMERA_DEVICE_EVENT.CAMERA_VCA_EVENTS,
    ...NVR_DEVICE_EVENT.NVR_VCA_EVENTS,
    ...VSS_DEVICE_EVENT.VCA_EVENTS])],
  GENERAL_EVENTS: [...new Set([...CAMERA_DEVICE_EVENT.CAMERA_GENERAL_EVENTS,
    ...NVR_DEVICE_EVENT.NVR_GENERAL_EVENTS,
    ...VSS_DEVICE_EVENT.GENERAL_EVENTS])],
  ONVIF_EVENTS: [...new Set([...NVR_DEVICE_EVENT.NVR_ONVIF_EVENTS])],
  EVENTS: [...new Set([
    ...CAMERA_DEVICE_EVENT.CAMERA_VCA_EVENTS,
    ...NVR_DEVICE_EVENT.NVR_VCA_EVENTS,
    ...CAMERA_DEVICE_EVENT.CAMERA_GENERAL_EVENTS,
    ...NVR_DEVICE_EVENT.NVR_GENERAL_EVENTS,
    ...NVR_DEVICE_EVENT.NVR_ONVIF_EVENTS,
    ...VSS_DEVICE_EVENT.VCA_EVENTS,
    ...VSS_DEVICE_EVENT.GENERAL_EVENTS,
  ])],
});

const SYSTEM_EVENT = Object.freeze({
  ...CAMERA_SYSTEM_EVENT,
  ...NVR_SYSTEM_EVENT,
  ...VSS_SYSTEM_EVENT,
  I18N: {
    ...CAMERA_SYSTEM_EVENT.I18N,
    ...NVR_SYSTEM_EVENT.I18N,
    ...VSS_SYSTEM_EVENT.I18N,
  },
  ICONS: {
    ...CAMERA_SYSTEM_EVENT.ICONS,
    ...NVR_SYSTEM_EVENT.ICONS,
    ...VSS_SYSTEM_EVENT.ICONS,
  },
  EVENTS: [
    ...CAMERA_SYSTEM_EVENT.CAMERA_SYSTEM_EVENTS,
    ...CAMERA_SYSTEM_EVENT.GENERAL_EVENTS,
    ...NVR_SYSTEM_EVENT.NVR_SYSTEM_EVENTS,
    ...VSS_SYSTEM_EVENT.VSS_SYSTEM_EVENTS,
  ],
});

const VCA_CAPABILITY_TYPE = Object.freeze({
  OBJECT_ATTRIBUTE: 'ObjectAttribute',
  HUMAN_SEARCH: 'HumanSearch',
  FACE_SEARCH: 'FaceSearch',
  PEOPLE_EVENT: 'PeopleEvent',
  MISSING_OBJECT_EVENT: 'MissingObjectEvent',
  UNATTENDED_OBJECT_EVENT: 'UnattendedObjectEvent',
  VEHICLE_EVENT: 'VehicleEvent',
  TAMPERING_EVENT: 'TamperingEvent',
  LICENSE_PLATE_RECOGNITION_EVENT: 'LicensePlateRecognitionEvent',
  FACE_RECOGNITION_EVENT: 'FaceRecognitionEvent',
  SPOT_OCCUPANCY_EVENT: 'SpotOccupancyEvent',
  PPE_DETECTION_EVENT: 'PPEDetectionEvent',
  THINK_ALERT_EVENT: 'ThinkAlertEvent',
  FALL_DETECTION_EVENT: 'FallDetectionEvent',
  LPR_PROFILE: 'LPRProfile',
});

const VCA_RULE_ENGINE_TYPE = Object.freeze({
  INTRUSION_DETECTION: 'IntrusionDetection',
  LINE_CROSSING_DETECTION: 'LineCrossingDetection',
  LOITERING_DETECTION: 'LoiteringDetection',
  MISSING_OBJECT_DETECTION: 'MissingObjectDetection',
  UNATTENDED_OBJECT_DETECTION: 'UnattendedObjectDetection',
  LICENSE_PLATE_RECOGNITION: 'LicensePlateRecognition',
  FACE_RECOGNITION: 'FaceRecognition',
  SPOT_OCCUPANCY: 'SpotOccupancy',
  PPE_DETECTION: 'PPEDetection',
  FALL_DETECTION: 'FallDetection',
  THINK_ALERT: 'ThinkAlert',
});

const VCA_RULE_ENGINE_SETTING_TYPE = Object.freeze({ EXCLUDED_AREA: 'ExclusiveArea' });

const RULE_TYPE = Object.freeze({
  DEFAULT_RULE_TYPE_LIST: Object.freeze([
    VCA_RULE_ENGINE_TYPE.INTRUSION_DETECTION,
    VCA_RULE_ENGINE_TYPE.LINE_CROSSING_DETECTION,
    VCA_RULE_ENGINE_TYPE.LOITERING_DETECTION
  ]),
  VCA_CAPABILITY_EVENT: [
    {
      event: VCA_CAPABILITY_TYPE.MISSING_OBJECT_EVENT,
      value: VCA_RULE_ENGINE_TYPE.MISSING_OBJECT_DETECTION
    },
    {
      event: VCA_CAPABILITY_TYPE.UNATTENDED_OBJECT_EVENT,
      value: VCA_RULE_ENGINE_TYPE.UNATTENDED_OBJECT_DETECTION
    },
    {
      event: VCA_CAPABILITY_TYPE.LICENSE_PLATE_RECOGNITION_EVENT,
      value: VCA_RULE_ENGINE_TYPE.LICENSE_PLATE_RECOGNITION
    },
    {
      event: VCA_CAPABILITY_TYPE.FACE_RECOGNITION_EVENT,
      value: VCA_RULE_ENGINE_TYPE.FACE_RECOGNITION
    },
    {
      event: VCA_CAPABILITY_TYPE.SPOT_OCCUPANCY_EVENT,
      value: VCA_RULE_ENGINE_TYPE.SPOT_OCCUPANCY
    },
    {
      event: VCA_CAPABILITY_TYPE.PPE_DETECTION_EVENT,
      value: VCA_RULE_ENGINE_TYPE.PPE_DETECTION
    },
    {
      event: VCA_CAPABILITY_TYPE.THINK_ALERT_EVENT,
      value: VCA_RULE_ENGINE_TYPE.THINK_ALERT
    },
    {
      event: VCA_CAPABILITY_TYPE.FALL_DETECTION_EVENT,
      value: VCA_RULE_ENGINE_TYPE.FALL_DETECTION
    }
  ]
});

const buildSupportDeviceMap = (eventMappings) => {
  const result = {};

  eventMappings.forEach(({ events, supportedDevices }) => {
    events.forEach((event) => {
      result[event] = result[event] || [];
      supportedDevices.forEach((type) => {
        if (!result[event].includes(type)) {
          result[event].push(type);
        }
      });
    });
  });

  return result;
};

const MULTIPLE_SELECT_DEVICE_EVENT_OPTIONS = (deviceTypes = [], $t) => {
  const supportDeviceMap = buildSupportDeviceMap([
    {
      events: [
        ...CAMERA_DEVICE_EVENT.CAMERA_VCA_EVENTS,
        ...CAMERA_DEVICE_EVENT.CAMERA_GENERAL_EVENTS
      ],
      supportedDevices: [DEVICE_TYPE.CAMERA].filter((type) => deviceTypes.includes(type))
    },
    {
      events: [
        ...NVR_DEVICE_EVENT.NVR_VCA_EVENTS,
        ...NVR_DEVICE_EVENT.NVR_GENERAL_EVENTS,
        ...NVR_DEVICE_EVENT.NVR_ONVIF_EVENTS
      ],
      supportedDevices: [DEVICE_TYPE.NVR].filter((type) => deviceTypes.includes(type))
    },
    {
      events: [
        ...VSS_DEVICE_EVENT.VCA_EVENTS,
        ...VSS_DEVICE_EVENT.GENERAL_EVENTS
      ],
      supportedDevices: [DEVICE_TYPE.VSS].filter((type) => deviceTypes.includes(type))
    }
  ]);

  const hasNVR = deviceTypes.includes(DEVICE_TYPE.NVR);
  const hasVSS = deviceTypes.includes(DEVICE_TYPE.VSS);

  const vcaEvents = [
    ...CAMERA_DEVICE_EVENT.CAMERA_VCA_EVENTS,
    ...(hasNVR ? NVR_DEVICE_EVENT.NVR_VCA_EVENTS : []),
    ...(hasVSS ? VSS_DEVICE_EVENT.VCA_EVENTS : []),
  ];

  const generalEvents = [
    ...CAMERA_DEVICE_EVENT.CAMERA_GENERAL_EVENTS,
    ...(hasNVR ? NVR_DEVICE_EVENT.NVR_GENERAL_EVENTS : []),
    ...(hasVSS ? VSS_DEVICE_EVENT.GENERAL_EVENTS : []),
  ];

  const onvifEvents = [
    ...(hasNVR ? NVR_DEVICE_EVENT.NVR_ONVIF_EVENTS : []),
  ];

  const eventsWithIcon = new Set([
    ...NVR_DEVICE_EVENT.NVR_VCA_EVENTS,
    ...NVR_DEVICE_EVENT.NVR_GENERAL_EVENTS,
    ...NVR_DEVICE_EVENT.NVR_ONVIF_EVENTS,
    ...VSS_DEVICE_EVENT.VCA_EVENTS,
    ...VSS_DEVICE_EVENT.GENERAL_EVENTS,
  ]);

  return [
    {
      groupLabel: EVENT_LABELS.VCA($t),
      groupValues: generateDropdownOptionValues({
        $t,
        list: [...new Set(vcaEvents)],
        i18n: DEVICE_EVENT.I18N,
        eventsWithIcon,
        supportDeviceMap
      }),
    },
    {
      groupLabel: EVENT_LABELS.GENERAL_EVENTS($t),
      groupValues: generateDropdownOptionValues({
        $t,
        list: [...new Set(generalEvents)],
        i18n: DEVICE_EVENT.I18N,
        eventsWithIcon,
        supportDeviceMap
      }),
    },
    {
      groupLabel: EVENT_LABELS.ONVIF_EVENTS($t),
      groupValues: generateDropdownOptionValues({
        $t,
        list: [...new Set(onvifEvents)],
        i18n: DEVICE_EVENT.I18N,
        eventsWithIcon,
        supportDeviceMap
      }),
    }
  ];
};

const MULTIPLE_SELECT_SYSTEM_EVENT_OPTIONS = (deviceTypes = [], $t, allowVssPrompt = false) => {
  const supportDeviceMap = buildSupportDeviceMap([
    {
      events: Object.values(GENERAL_SYSTEM_EVENT_TYPE),
      supportedDevices: [
        DEVICE_TYPE.CAMERA,
        DEVICE_TYPE.NVR,
        DEVICE_TYPE.VSS
      ]
    },
    {
      events: Object.values(SYSTEM_EVENT_TYPES),
      supportedDevices: [DEVICE_TYPE.CAMERA].filter((type) => deviceTypes.includes(type))
    },
    {
      events: Object.values(NVR_SYSTEM_EVENT_TYPES),
      supportedDevices: [DEVICE_TYPE.NVR].filter((type) => deviceTypes.includes(type))
    },
    {
      events: Object.values(VSS_SYSTEM_EVENT_TYPES),
      supportedDevices: [DEVICE_TYPE.VSS].filter((type) => deviceTypes.includes(type))
    }
  ]);

  const hasNVR = deviceTypes.includes(DEVICE_TYPE.NVR);
  const hasVSS = deviceTypes.includes(DEVICE_TYPE.VSS);

  const generalSystemEvents = [
    ...SYSTEM_EVENT.GENERAL_EVENTS,
  ];

  const cameraSystemEvents = [
    ...SYSTEM_EVENT.CAMERA_SYSTEM_EVENTS,
  ];

  const vssAndnvrSystemEvents = [
    ...(hasNVR ? SYSTEM_EVENT.NVR_SYSTEM_EVENTS : []),
    ...(hasVSS ? SYSTEM_EVENT.VSS_SYSTEM_EVENTS : []),
  ];

  const eventsWithIcon = new Set([
    ...(hasNVR ? SYSTEM_EVENT.NVR_SYSTEM_EVENTS : []),
    ...(hasVSS ? SYSTEM_EVENT.VSS_SYSTEM_EVENTS : []),
  ]);

  return [
    {
      groupLabel: EVENT_LABELS.GENERAL_SYSTEM_EVENTS($t),
      groupValues: generateDropdownOptionValues({
        $t,
        list: [...new Set(generalSystemEvents)],
        i18n: SYSTEM_EVENT.I18N,
        eventsWithIcon,
        supportDeviceMap
      })
    },
    {
      groupLabel: EVENT_LABELS.CAMERA_SYSTEM_EVENTS($t),
      groupValues: generateDropdownOptionValues({
        $t,
        list: [...new Set(cameraSystemEvents)],
        i18n: SYSTEM_EVENT.I18N,
        eventsWithIcon,
        supportDeviceMap
      })
    },
    ...(hasNVR || hasVSS ? [{
      groupLabel: EVENT_LABELS.NVR_VSS_SYSTEM_EVENTS($t, allowVssPrompt),
      groupValues: generateDropdownOptionValues({
        $t,
        list: [...new Set(vssAndnvrSystemEvents)],
        i18n: SYSTEM_EVENT.I18N,
        eventsWithIcon,
        supportDeviceMap
      })
    }] : []),
  ];
};

const ALARM_SYSTEM_EVENT_ICONS = Object.freeze({
  [NVR_SYSTEM_EVENT_TYPES.SYSTEM_DI]: 'device_DI',
  [NVR_SYSTEM_EVENT_TYPES.SYSTEM_DO]: 'device_DO',
  [NVR_SYSTEM_EVENT_TYPES.DISK_FAILURE]: 'storage_alert',
  [NVR_SYSTEM_EVENT_TYPES.DISK_FULL]: 'storage_alert',
  [NVR_SYSTEM_EVENT_TYPES.POE_ERROR]: 'PoE_alert',
  [NVR_SYSTEM_EVENT_TYPES.CHANNEL_ONLINE]: 'online',
  [NVR_SYSTEM_EVENT_TYPES.CHANNEL_OFFLINE]: 'offline',
  [NVR_SYSTEM_EVENT_TYPES.RECORDING_INTERRUPTED]: 'recording_alert',
  [GENERAL_SYSTEM_EVENT_TYPE.CONNECT]: 'online',
  [GENERAL_SYSTEM_EVENT_TYPE.DISCONNECT]: 'offline',
  [GENERAL_SYSTEM_EVENT_TYPE.FIRMWARE_UPGRADE_FAILED]: 'firmware_alert',
  [SYSTEM_EVENT_TYPES.SD_CARD_ERROR]: 'sdcard_alert',
  [SYSTEM_EVENT_TYPES.SD_CARD_LIFE_WARNING]: 'sdcard_alert',
  [SYSTEM_EVENT_TYPES.RECORDING_ERROR]: 'recording_alert',
});

const MESSAGE_TYPE = Object.freeze({
  DEVICE: 'device',
  SYSTEM: 'system',
  ACCESS: 'access',
  SENSOR: 'sensor',
});

const MESSAGE_URL_TYPE_MAP = Object.freeze({
  [MESSAGE_TYPE.ACCESS]: 'access-control',
  [MESSAGE_TYPE.SENSOR]: 'smart-sensor',
});

const FALSE_ALARM_EVENT_LIST = Object.freeze([
  ...CAMERA_DEVICE_EVENT.CAMERA_VCA_EVENTS,
  ...NVR_DEVICE_EVENT.NVR_VCA_EVENTS,
  GENERAL_DEVICE_EVENT_TYPE.TAMPERING
]);

export default {
  RULE_TYPE,
  MESSAGE_TYPE,
  MESSAGE_URL_TYPE_MAP,
  DEVICE_EVENT,
  SYSTEM_EVENT,
  EVENT_TIME_RANGE,
  CAMERA_SYSTEM_EVENT,
  CAMERA_DEVICE_EVENT,
  NVR_DEVICE_EVENT,
  NVR_SYSTEM_EVENT,
  ALARM_SYSTEM_EVENT_ICONS,
  MULTIPLE_SELECT_DEVICE_EVENT_OPTIONS,
  MULTIPLE_SELECT_SYSTEM_EVENT_OPTIONS,
  EVENT_LABELS,
  VCA_EVENT_TYPE,
  VCA_CAPABILITY_TYPE,
  VCA_RULE_ENGINE_TYPE,
  FALSE_ALARM_EVENT_LIST,
  VSS_DEVICE_EVENT,
  VSS_SYSTEM_EVENT,
  GENERAL_SYSTEM_EVENT_TYPE,
  SYSTEM_EVENT_TYPES,
  NVR_SYSTEM_EVENT_TYPES,
  VSS_SYSTEM_EVENT_TYPES,
  VCA_RULE_ENGINE_SETTING_TYPE,
  buildSupportDeviceMap,
  NVR_ONVIF_EVENT_TYPES,
};
